import { resolveAccountCreatedIdentity } from './accountCreatedEnrichment';
import { buildPartnerLayoutTree } from './layoutTreeUtils';
import { collectSubtreePlans } from './buildLayoutStatsWithLineage';

export const layoutIdsForInquiryRow = (row) => {
  const ids = new Set();
  const galleryId = row?.detail?.galleryId;
  const uniqueId = row?.detail?.uniqueId;
  if (galleryId) ids.add(String(galleryId));
  if (uniqueId) ids.add(String(uniqueId));
  return [...ids];
};

/** Display name for a catalog plan's author (original uploader). */
export const authorLabelFromPlan = (plan) => {
  const author = plan?.author;
  if (author?.name) return author.name;
  if (author?.email) return author.email;
  return null;
};

/**
 * Map layout mongo id / uuid → lineage root (original agent upload).
 * Built once per catalog load for inquiry drill-down uploader labels.
 */
export const buildInquiryRootUploaderLookup = (catalogLayouts) => {
  const rootByLayoutKey = new Map();
  const treeRoots = buildPartnerLayoutTree(catalogLayouts || []);

  treeRoots.forEach((root) => {
    if (!root?._id) return;
    collectSubtreePlans(root).forEach((plan) => {
      if (plan._id) rootByLayoutKey.set(String(plan._id), root);
      if (plan.uuid) rootByLayoutKey.set(String(plan.uuid), root);
    });
  });

  return rootByLayoutKey;
};

/** Original (parent) uploader from partner catalog lineage. */
export const resolveRootUploaderFromCatalog = (row, rootByLayoutKey) => {
  if (!rootByLayoutKey?.size) return null;
  const ids = layoutIdsForInquiryRow(row);
  for (let i = 0; i < ids.length; i += 1) {
    const root = rootByLayoutKey.get(ids[i]);
    if (root) {
      const label = authorLabelFromPlan(root);
      if (label) return label;
    }
  }
  return null;
};

/** Fallback when catalog has not loaded — match upload/ready event on same layout id. */
export const findUploaderEventForInquiry = (row, allRows) => {
  const ids = layoutIdsForInquiryRow(row);
  if (!ids.length || !Array.isArray(allRows)) return null;

  for (let i = 0; i < allRows.length; i += 1) {
    const candidate = allRows[i];
    const ev = candidate?.meta?.event;
    if (ev !== 'floorplan_uploaded' && ev !== 'gallery_ready') continue;
    const candidateId = candidate?.detail?.galleryId || candidate?.detail?.uniqueId;
    if (candidateId && ids.includes(String(candidateId))) return candidate;
  }
  return null;
};

export const resolveInquirerLabel = (row) => {
  const email = row?.detail?.inquirerEmail;
  if (typeof email === 'string' && email.trim()) return email.trim();
  return 'Someone';
};

export const resolveUploaderLabel = (
  row,
  allRows,
  signupUserDirectory,
  rootByLayoutKey = null,
  { catalogLookupLoading = false } = {}
) => {
  const fromCatalog = resolveRootUploaderFromCatalog(row, rootByLayoutKey);
  if (fromCatalog) return fromCatalog;
  if (catalogLookupLoading) return null;

  const uploaderRow = findUploaderEventForInquiry(row, allRows);
  if (!uploaderRow) return 'Unknown agent';

  const { email, name } = resolveAccountCreatedIdentity(
    uploaderRow,
    allRows,
    signupUserDirectory
  );
  if (name) return name;
  if (email) return email;

  const metaUser = uploaderRow?.meta?.user;
  if (metaUser && metaUser !== 'guest' && metaUser !== 'anonymous') {
    return String(metaUser);
  }
  return 'Unknown agent';
};

/** "{Inquirer} inquired about {Uploader}'s listing" — null while uploader lookup pending. */
export const formatInquiryActivityLine = (
  row,
  allRows,
  signupUserDirectory,
  rootByLayoutKey = null,
  { catalogLookupLoading = false } = {}
) => {
  const inquirer = resolveInquirerLabel(row);
  const uploader = resolveUploaderLabel(
    row,
    allRows,
    signupUserDirectory,
    rootByLayoutKey,
    { catalogLookupLoading }
  );
  if (uploader == null) return null;
  return `${inquirer} expressed interest in ${uploader}'s listing`;
};
